import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Upload, X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import StandardizedIcon from '@/components/wizard/StandardizedIcon';
import OptimizedImage from '@/components/OptimizedImage';

// Mobile horizontal-scroll wrapper for the category TabsList.
// Shows left/right chevron arrows on mobile to indicate scrollability and
// allow tap-to-scroll. Hidden on lg+ where the list is vertical.
const CategoryTabsScroller: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLElement | null>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = useCallback(() => {
    const s = scrollerRef.current;
    if (!s) return;
    setCanLeft(s.scrollLeft > 4);
    setCanRight(s.scrollLeft + s.clientWidth < s.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const scroller = wrap.querySelector<HTMLElement>('[role="tablist"]');
    if (!scroller) return;
    scrollerRef.current = scroller;

    const active = scroller.querySelector<HTMLElement>('[data-state="active"]');
    if (active && 'scrollIntoView' in active) {
      try {
        active.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'auto' });
      } catch {}
    }
    updateArrows();
    scroller.addEventListener('scroll', updateArrows, { passive: true });
    const ro = new ResizeObserver(updateArrows);
    ro.observe(scroller);
    return () => {
      scroller.removeEventListener('scroll', updateArrows);
      ro.disconnect();
    };
  }, [updateArrows]);

  const scrollBy = (dir: 1 | -1) => {
    const s = scrollerRef.current;
    if (!s) return;
    s.scrollBy({ left: dir * Math.max(160, s.clientWidth * 0.7), behavior: 'smooth' });
  };

  return (
    <div ref={wrapRef} className="relative lg:contents">
      {children}
      {/* Mobile-only scroll affordances */}
      <button
        type="button"
        aria-label="Scroll categories left"
        onClick={() => scrollBy(-1)}
        className={`lg:hidden absolute left-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-background/90 border border-border shadow-md flex items-center justify-center transition-opacity ${canLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      <button
        type="button"
        aria-label="Scroll categories right"
        onClick={() => scrollBy(1)}
        className={`lg:hidden absolute right-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-background/90 border border-border shadow-md flex items-center justify-center transition-opacity ${canRight ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};


// Import helicopter icons
import helicopterMainPhotoIcon from '@/assets/helicopter-main-photo-icon.png';
import helicopterFrontViewIcon from '@/assets/helicopter-front-view-icon.png';
import helicopterSideViewIcon from '@/assets/helicopter-side-view-icon.png';
import helicopterInteriorIcon from '@/assets/helicopter-interior-icon.png';
import helicopterAdditionalIcon from '@/assets/helicopter-additional-icon.png';
import helicopterVideoIcon from '@/assets/helicopter-video-icon.png';
import helicopterCockpitIcon from '@/assets/helicopter-cockpit-icon.png';

interface MediaItem {
  id: string;
  file_url: string;
  media_type: string;
  metadata?: {
    category?: string;
  };
  display_order?: number;
}

interface ProfessionalPhotoGalleryProps {
  media: MediaItem[];
  legacyImages?: string[]; // Support for legacy images array
  className?: string;
  canManagePhotos?: boolean;
  onAddPhotos?: () => void;
}

// Lightbox image with category metadata
interface LightboxImage {
  url: string;
  category: string;
  icon: string;
  label: string;
}

// Lightbox Component
const Lightbox: React.FC<{
  images: LightboxImage[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}> = ({ images, currentIndex, onClose, onNavigate }) => {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft' && currentIndex > 0) onNavigate(currentIndex - 1);
    if (e.key === 'ArrowRight' && currentIndex < images.length - 1) onNavigate(currentIndex + 1);
  }, [currentIndex, images.length, onClose, onNavigate]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  return (
    <div 
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Close button */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-4 right-4 z-10 text-white hover:bg-white/20 h-12 w-12"
        onClick={onClose}
        aria-label="Close gallery"
      >
        <X className="h-6 w-6" />
      </Button>

      {/* Image counter */}
      <div className="absolute top-4 left-4 z-10">
        <Badge variant="secondary" className="bg-white/20 text-white border-0 text-sm px-3 py-1">
          {currentIndex + 1} / {images.length}
        </Badge>
      </div>

      {/* Previous button */}
      {currentIndex > 0 && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20 h-14 w-14"
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(currentIndex - 1);
          }}
          aria-label="Previous photo"
        >
          <ChevronLeft className="h-8 w-8" />
        </Button>
      )}

      {/* Next button */}
      {currentIndex < images.length - 1 && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20 h-14 w-14"
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(currentIndex + 1);
          }}
          aria-label="Next photo"
        >
          <ChevronRight className="h-8 w-8" />
        </Button>
      )}

      {/* Main image */}
      <div 
        className="max-w-[90vw] max-h-[75vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <OptimizedImage
          src={images[currentIndex]?.url || ''}
          alt={`Photo ${currentIndex + 1}`}
          size="full"
          priority
          enableBlurUp={false}
          className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl"
        />
      </div>

      {/* Category thumbnail containers */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-3 max-w-[95vw] overflow-x-auto p-3 bg-black/60 rounded-xl backdrop-blur-md">
          {images.map((img, idx) => (
            <button
              key={idx}
              className={`flex flex-col items-center gap-1.5 p-2 rounded-xl transition-all flex-shrink-0 min-w-[80px] md:min-w-[100px] ${
                idx === currentIndex 
                  ? 'bg-white/15 ring-2 ring-[hsl(45,93%,47%)] shadow-lg shadow-[hsl(45,93%,47%)]/30' 
                  : 'bg-white/5 hover:bg-white/10 opacity-70 hover:opacity-100'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                onNavigate(idx);
              }}
            >
              {/* Category icon */}
              <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
                <StandardizedIcon
                  src={img.icon}
                  alt={img.label}
                  size="sm"
                  color="white"
                />
              </div>
              {/* Category label */}
              <span className="text-[10px] md:text-xs text-white/80 font-medium truncate max-w-[70px] md:max-w-[90px]">
                {img.label}
              </span>
              {/* Thumbnail */}
              <div className={`w-14 h-10 md:w-16 md:h-12 rounded-md overflow-hidden border ${
                idx === currentIndex ? 'border-[hsl(45,93%,47%)]' : 'border-white/20'
              }`}>
                <OptimizedImage src={img.url} alt="" size="thumbnail" enableBlurUp={false} className="w-full h-full object-cover" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ProfessionalPhotoGallery: React.FC<ProfessionalPhotoGalleryProps> = ({
  media,
  legacyImages = [],
  className = "",
  canManagePhotos = false,
  onAddPhotos
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Memoize allMedia to prevent recalculation on every render
  const allMedia = useMemo(() => 
    media.length > 0 
      ? media 
      : legacyImages.map((url, index) => ({
          id: `legacy-${index}`,
          file_url: url,
          media_type: 'image',
          display_order: index,
          metadata: {}
        })),
    [media, legacyImages]
  );

  // Memoize organized media and main photo URL to prevent infinite loops
  const { organizedMedia, mainPhotoUrl } = useMemo(() => {
    const organized: Record<string, string[]> = {};
    const mainUrl = allMedia.find(m => 
      m.metadata && 'category' in m.metadata && m.metadata.category === 'mainPhoto'
    )?.file_url || allMedia[0]?.file_url || '';
    
    // Group all media by their categories
    allMedia.forEach(item => {
      const category = (item.metadata && 'category' in item.metadata && item.metadata.category) || 'uncategorized';
      if (category !== 'mainPhoto') {
        if (!organized[category]) {
          organized[category] = [];
        }
        organized[category].push(item.file_url);
      }
    });
    
    // Add videos as separate category
    const videos = allMedia.filter(m => m.media_type === 'video').map(m => m.file_url);
    if (videos.length > 0) {
      organized.videos = videos;
    }
    
    return { organizedMedia: organized, mainPhotoUrl: mainUrl };
  }, [allMedia]);

  // Icon mapping for categories
  const categoryIconMap: Record<string, string> = {
    mainPhoto: helicopterMainPhotoIcon,
    frontView: helicopterFrontViewIcon,
    exterior: helicopterFrontViewIcon,
    cockpit: helicopterCockpitIcon,
    rightSide: helicopterSideViewIcon,
    leftSide: helicopterSideViewIcon,
    interior: helicopterInteriorIcon,
    additional: helicopterAdditionalIcon,
    engine: helicopterAdditionalIcon,
    videos: helicopterVideoIcon,
    uncategorized: helicopterAdditionalIcon
  };
  
  const categoryLabels: Record<string, string> = {
    mainPhoto: 'Main Photo',
    frontView: 'Front View',
    exterior: 'Exterior Views',
    cockpit: 'Cockpit',
    rightSide: 'Right Side',
    leftSide: 'Left Side',
    interior: 'Interior',
    additional: 'Additional Details',
    engine: 'Engine & Technical',
    videos: 'Videos',
    uncategorized: 'Other Photos'
  };
  
  // Build category config dynamically based on available media
  const categoryConfig = [
    ...Object.keys(organizedMedia)
      .filter(key => organizedMedia[key].length > 0)
      .map(key => ({
        key,
        label: categoryLabels[key] || key.charAt(0).toUpperCase() + key.slice(1),
        icon: categoryIconMap[key] || helicopterAdditionalIcon,
        description: `${organizedMedia[key].length} photos`
      }))
  ].filter(Boolean) as Array<{ key: string; label: string; icon: string; description: string }>;

  // Reorder categories: uncategorized last
  const availableCategories = categoryConfig.sort((a, b) => {
    if (a.key === 'uncategorized') return 1;
    if (b.key === 'uncategorized') return -1;
    return 0;
  });

  // Build flat array of all photos with category metadata for lightbox
  const buildAllLightboxImages = useCallback((): LightboxImage[] => {
    const images: LightboxImage[] = [];
    const seenUrls = new Set<string>();

    // Add main photo first
    if (mainPhotoUrl && !seenUrls.has(mainPhotoUrl)) {
      seenUrls.add(mainPhotoUrl);
      images.push({
        url: mainPhotoUrl,
        category: 'mainPhoto',
        icon: categoryIconMap['mainPhoto'],
        label: 'Hero'
      });
    }

    // Add organized media by category
    Object.entries(organizedMedia).forEach(([category, photos]) => {
      photos.forEach((url, idx) => {
        if (!seenUrls.has(url)) {
          seenUrls.add(url);
          const label = categoryLabels[category] || category;
          images.push({
            url,
            category,
            icon: categoryIconMap[category] || helicopterAdditionalIcon,
            label: photos.length > 1 ? `${label} ${idx + 1}` : label
          });
        }
      });
    });

    return images;
  }, [mainPhotoUrl, organizedMedia]);

  // Derive lightbox images from memoized data (no state, no useEffect needed)
  const allLightboxImages = useMemo(() => buildAllLightboxImages(), [buildAllLightboxImages]);

  const openLightbox = (photoUrl: string) => {
    const index = allLightboxImages.findIndex(img => img.url === photoUrl);
    setLightboxIndex(index >= 0 ? index : 0);
    setLightboxOpen(true);
  };

  const renderPhotoGrid = (photos: string[]) => {
    const isSingle = photos.length === 1;
    return (
      <div
        className={
          isSingle
            ? 'flex justify-center'
            : 'grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4'
        }
      >
        {photos.map((photo, index) => (
          <Card
            key={index}
            className={
              'overflow-hidden group cursor-pointer hover:shadow-lg transition-all duration-300 ' +
              (isSingle ? 'w-full max-w-2xl' : '')
            }
            onClick={() => openLightbox(photo)}
          >
            <div className={isSingle ? 'aspect-[4/3] sm:aspect-[16/10] relative' : 'aspect-square relative'}>
              <OptimizedImage
                src={photo}
                alt={`Photo ${index + 1}`}
                size={isSingle ? 'full' : 'thumbnail'}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute bottom-1.5 left-1.5 sm:bottom-2 sm:left-2">
                <Badge variant="secondary" className="bg-background/90 text-foreground text-xs px-1.5 py-0.5 sm:px-2 sm:py-1">
                  {index + 1}
                </Badge>
              </div>
              {/* Zoom indicator on hover */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <ZoomIn className="w-6 h-6 sm:w-8 sm:h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  };


  const renderMainPhoto = () => (
    <Card 
      className="overflow-hidden mb-4 sm:mb-8 cursor-pointer group"
      onClick={() => openLightbox(mainPhotoUrl)}
    >
      <div className="aspect-[4/3] sm:aspect-[16/10] relative">
        <OptimizedImage
          src={mainPhotoUrl}
          alt="Main listing photo"
          size="full"
          priority
          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
        />
        <div className="absolute top-2 left-2 sm:top-4 sm:left-4">
          <Badge className="bg-primary text-primary-foreground gap-1.5 text-xs sm:text-sm sm:gap-2">
            <StandardizedIcon
              src={helicopterMainPhotoIcon}
              alt="Main photo"
              size="sm"
              color="currentColor"
            />
            <span className="hidden sm:inline">Hero Image</span>
            <span className="sm:hidden">Hero</span>
          </Badge>
        </div>
        {/* Zoom indicator on hover */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
          <ZoomIn className="w-10 h-10 sm:w-12 sm:h-12 text-white opacity-0 group-hover:opacity-80 transition-opacity" />
        </div>
      </div>
    </Card>
  );

  if (allMedia.length === 0) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="text-center py-16 bg-muted/20 rounded-xl border-2 border-dashed">
          <StandardizedIcon
            src={helicopterMainPhotoIcon}
            alt="No photos"
            size="xl"
            className="opacity-30 mb-4"
          />
          <p className="text-muted-foreground mb-4">No photos available</p>
          {canManagePhotos && onAddPhotos && (
            <>
              <Button 
                onClick={onAddPhotos}
                size="lg"
                className="mb-3"
              >
                <Upload className="w-4 h-4 mr-2" />
                Add Photos
              </Button>
              <p className="text-xs text-muted-foreground">Only the listing owner can add photos</p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Lightbox */}
      {lightboxOpen && allLightboxImages.length > 0 && (
        <Lightbox
          images={allLightboxImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
          onNavigate={setLightboxIndex}
        />
      )}

      <div className={`space-y-8 ${className}`}>
        {/* Main Photo */}
        {mainPhotoUrl && renderMainPhoto()}

        {/* Categorized Photos */}
        {availableCategories.length > 0 && (
          <Card className="gradient-card border overflow-hidden">
            <div className="p-4 lg:p-6">
              <div className="flex items-center gap-3 mb-4 lg:mb-6">
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-primary/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <StandardizedIcon
                    src={helicopterAdditionalIcon}
                    alt="Gallery"
                    size="md"
                    color="hsl(var(--primary))"
                  />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg lg:text-xl font-bold">Photo Gallery</h3>
                  <p className="text-sm text-muted-foreground truncate">Detailed helicopter views and features</p>
                </div>
              </div>

              <Tabs defaultValue={availableCategories[0]?.key} className="w-full">
                {/* Mobile: horizontal scrollable tabs first, then content */}
                {/* Desktop: Two-column layout with filters on right */}
                <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
                  
                  {/* Category filters - horizontal scroll on mobile, vertical on desktop */}
                  <div className="lg:w-80 lg:order-last">
                    <h4 className="font-medium text-sm text-muted-foreground mb-2 px-1 lg:mb-3 lg:px-2">Photo Categories</h4>
                    <CategoryTabsScroller>
                      <TabsList className="h-auto p-1 px-10 lg:px-1 bg-muted w-full flex lg:flex-col lg:space-y-1 overflow-x-auto lg:overflow-x-visible gap-1 lg:gap-0 scrollbar-hide justify-start lg:justify-stretch scroll-smooth">
                        {availableCategories.map((category) => {
                          const photoCount = category.key === 'mainPhoto'
                            ? (mainPhotoUrl ? 1 : 0)
                            : organizedMedia[category.key]?.length || 0;

                          return (
                            <TabsTrigger
                              key={category.key}
                              value={category.key}
                              className="flex-shrink-0 lg:w-full justify-start gap-2 lg:gap-3 h-auto px-3 py-2 lg:p-4 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm whitespace-nowrap lg:whitespace-normal"
                            >
                              <div className="flex items-center gap-2 lg:gap-3 lg:flex-1">
                                <div className="w-7 h-7 lg:w-10 lg:h-10 rounded-lg bg-muted/30 flex items-center justify-center flex-shrink-0 p-0.5 lg:p-1">
                                  <StandardizedIcon
                                    src={category.icon}
                                    alt={category.label}
                                    size="sm"
                                    color="currentColor"
                                    fit={true}
                                  />
                                </div>
                                <div className="text-left">
                                  <div className="font-medium text-xs lg:text-sm">{category.label}</div>
                                  <div className="text-xs text-muted-foreground hidden lg:block">{photoCount} photo{photoCount !== 1 ? 's' : ''}</div>
                                </div>
                              </div>
                            </TabsTrigger>
                          );
                        })}
                      </TabsList>
                    </CategoryTabsScroller>
                  </div>

                  {/* Main photo grid area */}
                  <div className="flex-1 min-w-0">
                    {availableCategories.map((category) => {
                      const photos = category.key === 'mainPhoto' 
                        ? [mainPhotoUrl]
                        : organizedMedia[category.key] || [];
                      
                      return (
                        <TabsContent key={category.key} value={category.key} className="space-y-4 mt-0">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4 lg:mb-6 p-3 lg:p-4 bg-muted/20 rounded-lg border">
                            <StandardizedIcon
                              src={category.icon}
                              alt={category.label}
                              size="sm"
                              color="hsl(var(--muted-foreground))"
                            />
                            <span className="truncate">{category.description} • {photos.length} photo{photos.length !== 1 ? 's' : ''}</span>
                          </div>
                          {renderPhotoGrid(photos)}
                        </TabsContent>
                      );
                    })}
                  </div>
                </div>
              </Tabs>
            </div>
          </Card>
        )}
      </div>
    </>
  );
};

export default ProfessionalPhotoGallery;